// Push notification utilities for deadline reminders
// Supports multiple reminder intervals: 7 days, 3 days, 1 day before deadline

const REMINDER_INTERVALS = [
    { days: 7, label: '1 week before' },
    { days: 3, label: '3 days before' },
    { days: 1, label: '1 day before' }
];

// Local storage key for notification preferences
const PREFS_KEY = 'notification_preferences';

// Check if push notifications are supported
export const isPushSupported = () => {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// Get notification preferences from localStorage
export const getNotificationPrefs = () => {
    try {
        const stored = localStorage.getItem(PREFS_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to parse notification prefs:', e);
    }
    
    // Default preferences
    return {
        enabled: false,
        intervals: {
            7: true,  // 7 days before
            3: true,  // 3 days before
            1: true   // 1 day before
        },
        criticalOnly: false
    };
};

// Save notification preferences
export const saveNotificationPrefs = (prefs) => {
    try {
        localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
        return true;
    } catch (e) {
        console.error('Failed to save notification prefs:', e);
        return false;
    }
};

// Request notification permission
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.log('Notifications not supported');
        return 'denied';
    }

    if (Notification.permission === 'granted') {
        return 'granted';
    }

    if (Notification.permission !== 'denied') {
        const permission = await Notification.requestPermission();
        return permission;
    }

    return Notification.permission;
};

// Show a local notification
export const showNotification = async (title, options = {}) => {
    const permission = await requestNotificationPermission();
    
    if (permission !== 'granted') {
        console.log('Notification permission denied');
        return false;
    }

    const defaultOptions = {
        icon: '/icons/icon-192x192.png',
        badge: '/icons/icon-72x72.png',
        vibrate: [100, 50, 100],
        requireInteraction: false,
        tag: options.tag || `notification-${Date.now()}`,
        ...options
    };

    try {
        // Use service worker notification if available (works in background)
        if ('serviceWorker' in navigator) {
            const registration = await navigator.serviceWorker.ready;
            await registration.showNotification(title, defaultOptions);
            return true;
        }

        // Fallback to regular notification
        new Notification(title, defaultOptions);
        return true;
    } catch (error) {
        console.error('Failed to show notification:', error);
        return false;
    }
};

// Calculate time until a specific date
const getTimeUntil = (dateStr) => {
    const targetDate = new Date(dateStr);
    targetDate.setHours(9, 0, 0, 0); // Send reminders at 9 AM
    const now = new Date();
    return targetDate.getTime() - now.getTime();
};

// Get days until deadline
const getDaysUntil = (dateStr) => {
    const targetDate = new Date(dateStr);
    const now = new Date();
    targetDate.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    return Math.ceil((targetDate - now) / (1000 * 60 * 60 * 24));
};

// Schedule a single notification for a reminder at a specific interval
export const scheduleReminderNotification = (reminder, daysBefore) => {
    const dueDate = new Date(reminder.due_date);
    dueDate.setHours(9, 0, 0, 0); // 9 AM on reminder day
    
    // Calculate when to show the notification (daysBefore before due date)
    const notifyAt = new Date(dueDate.getTime() - (daysBefore * 24 * 60 * 60 * 1000));
    const now = new Date();

    // If notification time has passed, don't schedule
    if (notifyAt <= now) {
        return null;
    }

    const delay = notifyAt.getTime() - now.getTime();

    // Don't schedule if delay is more than 7 days (browser tab might close)
    // We'll reschedule on next app load
    if (delay > 7 * 24 * 60 * 60 * 1000) {
        return null;
    }

    let daysText;
    if (daysBefore === 1) {
        daysText = 'tomorrow';
    } else if (daysBefore === 7) {
        daysText = 'in 1 week';
    } else {
        daysText = `in ${daysBefore} days`;
    }

    // Schedule the notification
    const timeoutId = setTimeout(() => {
        const title = reminder.is_critical 
            ? `⚠️ Critical Deadline: ${reminder.title}`
            : `📅 Upcoming Deadline: ${reminder.title}`;
        
        showNotification(title, {
            body: `Due ${daysText}${reminder.case_name ? ` • ${reminder.case_name}` : ''}`,
            tag: `reminder-${reminder.id}-${daysBefore}d`,
            requireInteraction: reminder.is_critical,
            data: {
                reminderId: reminder.id,
                caseId: reminder.case_id,
                url: reminder.case_id ? `/case/${reminder.case_id}` : '/dashboard'
            }
        });
    }, delay);

    return timeoutId;
};

// Store scheduled notification IDs for cleanup
const scheduledNotifications = new Map();

// Schedule notifications for all reminders based on preferences
export const scheduleAllReminders = (reminders) => {
    const prefs = getNotificationPrefs();
    
    // If notifications are disabled, clear all and return
    if (!prefs.enabled || Notification.permission !== 'granted') {
        clearScheduledNotifications();
        return 0;
    }

    // Clear existing scheduled notifications
    clearScheduledNotifications();

    let scheduledCount = 0;

    reminders.forEach(reminder => {
        // Skip completed reminders
        if (reminder.is_completed) return;
        
        // Skip non-critical if criticalOnly is enabled
        if (prefs.criticalOnly && !reminder.is_critical) return;

        const daysUntil = getDaysUntil(reminder.due_date);

        // Schedule notifications for each enabled interval
        REMINDER_INTERVALS.forEach(({ days }) => {
            // Only schedule if this interval is enabled and we haven't passed it yet
            if (prefs.intervals[days] && daysUntil >= days) {
                const timeoutId = scheduleReminderNotification(reminder, days);
                if (timeoutId) {
                    scheduledNotifications.set(`${reminder.id}-${days}d`, timeoutId);
                    scheduledCount++;
                }
            }
        });

        // Also schedule same-day reminder for critical items at 8 AM if it's today
        if (reminder.is_critical && daysUntil === 0) {
            const now = new Date();
            const today8AM = new Date();
            today8AM.setHours(8, 0, 0, 0);
            
            if (now < today8AM) {
                const delay = today8AM.getTime() - now.getTime();
                const timeoutId = setTimeout(() => {
                    showNotification(`🚨 DUE TODAY: ${reminder.title}`, {
                        body: `This deadline is due today!${reminder.case_name ? ` • ${reminder.case_name}` : ''}`,
                        tag: `reminder-${reminder.id}-today`,
                        requireInteraction: true,
                        data: {
                            reminderId: reminder.id,
                            caseId: reminder.case_id,
                            url: reminder.case_id ? `/case/${reminder.case_id}` : '/dashboard'
                        }
                    });
                }, delay);
                scheduledNotifications.set(`${reminder.id}-today`, timeoutId);
                scheduledCount++;
            }
        }
    });

    console.log(`Scheduled ${scheduledCount} deadline notifications`);
    return scheduledCount;
};

// Clear all scheduled notifications
export const clearScheduledNotifications = () => {
    scheduledNotifications.forEach((timeoutId) => clearTimeout(timeoutId));
    scheduledNotifications.clear();
};

// Check and show immediate notifications for urgent deadlines
export const checkUrgentDeadlines = async (reminders) => {
    const prefs = getNotificationPrefs();
    
    if (!prefs.enabled || Notification.permission !== 'granted') {
        return;
    }

    const now = new Date();
    const overdueReminders = reminders.filter(r => {
        if (r.is_completed) return false;
        const daysUntil = getDaysUntil(r.due_date);
        return daysUntil < 0; // Overdue
    });

    const todayReminders = reminders.filter(r => {
        if (r.is_completed) return false;
        const daysUntil = getDaysUntil(r.due_date);
        return daysUntil === 0;
    });

    // Show overdue alert
    if (overdueReminders.length > 0) {
        const critical = overdueReminders.filter(r => r.is_critical);
        
        showNotification(
            `🚨 ${overdueReminders.length} Overdue Deadline${overdueReminders.length > 1 ? 's' : ''}`,
            {
                body: critical.length > 0 
                    ? `${critical.length} critical deadline${critical.length > 1 ? 's' : ''} past due!`
                    : `You have ${overdueReminders.length} deadline${overdueReminders.length > 1 ? 's' : ''} that need attention.`,
                tag: 'overdue-deadlines',
                requireInteraction: true
            }
        );
    }
};

// Get reminder intervals configuration
export const getReminderIntervals = () => REMINDER_INTERVALS;

// Test notification (for settings page)
export const sendTestNotification = async () => {
    const success = await showNotification('🔔 Test Notification', {
        body: 'Notifications are working correctly!',
        tag: 'test-notification'
    });
    return success;
};

export default {
    isPushSupported,
    getNotificationPrefs,
    saveNotificationPrefs,
    requestNotificationPermission,
    showNotification,
    scheduleReminderNotification,
    scheduleAllReminders,
    clearScheduledNotifications,
    checkUrgentDeadlines,
    getReminderIntervals,
    sendTestNotification
};
